import {
  getBrandDefaultCurrency,
  getBrandVatRate,
  normalizeBrandCurrency,
} from '../../shared/utils/brandCurrency';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';

function buildDefaultPaymentTerms() {
  return isSaudiBrand() ? '30 يوم من تاريخ الفاتورة' : '30 يوماً من تاريخ الفاتورة';
}

function buildDefaultLeadTime() {
  return isSaudiBrand() ? 21 : 7;
}

/** Canonical defaults — Arabic-first, aligned with achats submodules. */
export const DEFAULT_ACHATS_CONFIG = {
  // عام
  defaultCurrency: getBrandDefaultCurrency(),
  defaultPaymentTerms: buildDefaultPaymentTerms(),
  defaultLeadTimeDays: buildDefaultLeadTime(),
  defaultVatRate: getBrandVatRate(),
  itemsPerPage: 50,
  defaultView: 'dashboard',
  showSupplierRatings: true,
  lowStockAlertEnabled: true,

  // فهرس الموردين
  enableSupplierCatalog: true,
  supplierCatalogReferencePrefix: 'SCAT',
  autoGenerateSupplierCatalogReference: true,

  // طلبات الشراء
  enablePurchaseRequests: true,
  requestReferencePrefix: 'DREQ',
  autoGenerateRequestNumber: true,
  requireApprovalForRequests: true,

  // أوامر الشراء
  enablePurchaseOrders: true,
  orderReferencePrefix: 'CMDF',
  autoGenerateOrderNumber: true,
  requireApprovalForOrders: true,

  // استلام البضائع
  enablePurchaseReceipts: true,
  receiptReferencePrefix: 'REC',
  autoGenerateReceiptNumber: true,
  allowPartialReceipts: true,
  qualityCheckRequired: true,
  qualityInspectionReferencePrefix: 'QC',
  autoGenerateQualityInspectionReference: true,

  // مرتجعات المشتريات
  enablePurchaseReturns: true,
  returnReferencePrefix: 'RET',
  autoGenerateReturnReference: true,

  // المناقصات
  enableTenders: true,
  tenderReferencePrefix: 'AO',
  autoGenerateTenderReference: true,

  // مقارنة العروض
  enableQuoteComparison: true,
  quoteComparisonReferencePrefix: 'QCO',
  autoGenerateQuoteComparisonReference: true,

  // ربط بالميزانية
  enableBudgetLink: true,
  budgetReferencePrefix: 'BUD',
  autoGenerateBudgetReference: true,
  budgetAlertThreshold: 80,
};

const FRENCH_PAYMENT_TERMS = new Set([
  'net30',
  'net 30',
  'net-30',
  'net60',
  'net 60',
  'net-60',
  'net45',
  'net 45',
]);

const LEGACY_NOTIFICATION_KEYS = [
  'notifyOnRequestApproval',
  'notifyOnOrderDelivery',
  'notifyOnBudgetExceeded',
  'emailNotifications',
];

/** Map legacy API keys and scrub French / notification leftovers. */
export function normalizeAchatsConfig(raw = {}) {
  const merged = { ...DEFAULT_ACHATS_CONFIG, ...(raw && typeof raw === 'object' ? raw : {}) };

  merged.defaultCurrency = normalizeBrandCurrency(merged.defaultCurrency);
  merged.defaultVatRate = getBrandVatRate();

  const paymentTerms = String(merged.defaultPaymentTerms || '').trim();
  if (FRENCH_PAYMENT_TERMS.has(paymentTerms.toLowerCase())) {
    merged.defaultPaymentTerms = DEFAULT_ACHATS_CONFIG.defaultPaymentTerms;
  }

  if (raw.autoGenerateReceiptNumber != null && raw.autoGenerateReturnReference == null) {
    merged.autoGenerateReturnReference = raw.autoGenerateReceiptNumber;
  }
  if (raw.autoGenerateTenderReference == null && raw.autoGenerateRequestNumber != null) {
    merged.autoGenerateTenderReference = merged.autoGenerateTenderReference ?? raw.autoGenerateRequestNumber;
  }

  LEGACY_NOTIFICATION_KEYS.forEach((key) => {
    delete merged[key];
  });

  const pageSize = Number(merged.itemsPerPage);
  merged.itemsPerPage = [25, 50, 100].includes(pageSize)
    ? pageSize
    : DEFAULT_ACHATS_CONFIG.itemsPerPage;

  if (merged.defaultView !== 'dashboard' && merged.defaultView !== 'table') {
    merged.defaultView = DEFAULT_ACHATS_CONFIG.defaultView;
  }

  return merged;
}

export const ACHATS_ENTITY_PREFIX_MAP = {
  purchaseRequest: 'requestReferencePrefix',
  purchaseOrder: 'orderReferencePrefix',
  purchaseReceipt: 'receiptReferencePrefix',
  purchaseReturn: 'returnReferencePrefix',
  supplierCatalog: 'supplierCatalogReferencePrefix',
  tender: 'tenderReferencePrefix',
  quoteComparison: 'quoteComparisonReferencePrefix',
  purchaseBudget: 'budgetReferencePrefix',
  qualityInspection: 'qualityInspectionReferencePrefix',
};

export const ACHATS_ENTITY_AUTO_MAP = {
  purchaseRequest: 'autoGenerateRequestNumber',
  purchaseOrder: 'autoGenerateOrderNumber',
  purchaseReceipt: 'autoGenerateReceiptNumber',
  purchaseReturn: 'autoGenerateReturnReference',
  supplierCatalog: 'autoGenerateSupplierCatalogReference',
  tender: 'autoGenerateTenderReference',
  quoteComparison: 'autoGenerateQuoteComparisonReference',
  purchaseBudget: 'autoGenerateBudgetReference',
  qualityInspection: 'autoGenerateQualityInspectionReference',
};

export const ACHATS_ENTITY_ENABLE_MAP = {
  purchaseRequest: 'enablePurchaseRequests',
  purchaseOrder: 'enablePurchaseOrders',
  purchaseReceipt: 'enablePurchaseReceipts',
  purchaseReturn: 'enablePurchaseReturns',
  supplierCatalog: 'enableSupplierCatalog',
  tender: 'enableTenders',
  quoteComparison: 'enableQuoteComparison',
  purchaseBudget: 'enableBudgetLink',
};

/** Map achats config prefixes into settings.configuration.entityPrefixes keys. */
export function buildAchatsEntityPrefixes(config) {
  const c = normalizeAchatsConfig(config);
  return {
    purchaseRequest: c.requestReferencePrefix,
    purchaseOrder: c.orderReferencePrefix,
    purchaseReceipt: c.receiptReferencePrefix,
    purchaseReturn: c.returnReferencePrefix,
    supplierCatalog: c.supplierCatalogReferencePrefix,
    tender: c.tenderReferencePrefix,
    quoteComparison: c.quoteComparisonReferencePrefix,
    purchaseBudget: c.budgetReferencePrefix,
    qualityInspection: c.qualityInspectionReferencePrefix,
  };
}

export function getAchatsEntityPrefix(settings, entityKey, fallback) {
  const configKey = ACHATS_ENTITY_PREFIX_MAP[entityKey];
  const fromAchats = configKey ? settings?.achats?.[configKey] : null;
  if (fromAchats) return String(fromAchats).trim() || fallback;
  return settings?.configuration?.entityPrefixes?.[entityKey] || fallback;
}

export function isAchatsAutoReferenceEnabled(settings, entityKey) {
  const key = ACHATS_ENTITY_AUTO_MAP[entityKey];
  if (!key) return true;
  const val = settings?.achats?.[key];
  return val !== false;
}

export function isAchatsSubmoduleEnabled(settings, entityKey) {
  const key = ACHATS_ENTITY_ENABLE_MAP[entityKey];
  if (!key) return true;
  const val = settings?.achats?.[key];
  return val !== false;
}

export function getDefaultApprovalStatus(settings, entityKey) {
  const approvalMap = {
    purchaseRequest: 'requireApprovalForRequests',
    purchaseOrder: 'requireApprovalForOrders',
  };
  const approvalKey = approvalMap[entityKey];
  if (!approvalKey) return 'pending';
  const requiresApproval = settings?.achats?.[approvalKey];
  if (requiresApproval === false) return 'approved';
  return 'pending';
}
